import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  CheckCircle,
  Loader2,
  Phone,
  Mail
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const products = [
  { value: "strep-a", label: "Healgen Rapid Strep A Antigen Test (MRK250006)" },
  { value: "covid-flu", label: "Healgen COVID-19 / Flu A & B Combo Test (MRK240016)" },
  { value: "rsv", label: "Healgen Rapid Check RSV Antigen Test (MRK240002)" },
];

const volumeOptions = [
  { value: "under-100", label: "Under 100 tests/month" },
  { value: "100-500", label: "100 – 500 tests/month" },
  { value: "500-1000", label: "500 – 1,000 tests/month" },
  { value: "1000-plus", label: "1,000+ tests/month" },
];

export default function PricingRequestModal({ open, onOpenChange }) {
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    product_interest: "",
    monthly_volume: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      organization: "",
      email: "",
      phone: "",
      product_interest: "",
      monthly_volume: "",
      message: ""
    });
    setIsSubmitted(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form after modal close animation
    setTimeout(resetForm, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.organization || !formData.email || !formData.phone || !formData.product_interest || !formData.monthly_volume) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const productLabel = products.find(p => p.value === formData.product_interest)?.label || formData.product_interest;
      const volumeLabel = volumeOptions.find(v => v.value === formData.monthly_volume)?.label || formData.monthly_volume;

      const response = await axios.post(`${API}/pricing-request`, {
        name: formData.name,
        organization: formData.organization,
        email: formData.email,
        phone: formData.phone,
        product_interest: productLabel,
        monthly_volume: volumeLabel,
        message: formData.message || null
      });

      if (response.data.status === "success") {
        setIsSubmitted(true);
        toast.success("Your pricing request has been submitted!");
      }
    } catch (error) {
      console.error("Pricing request error:", error);
      toast.error("Failed to submit request. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        {isSubmitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <DialogHeader className="mb-4">
              <DialogTitle className="text-2xl text-center" data-testid="pricing-success-title">
                Request Received
              </DialogTitle>
              <DialogDescription className="text-center text-base mt-2">
                Thank you for your pricing request. A ClearPath representative will follow up within 1 business day.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-600 mb-2">For immediate assistance:</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a href="mailto:sales@clearpathrapid.com" className="flex items-center justify-center gap-2 text-sky-600 hover:text-sky-700 text-sm">
                  <Mail className="w-4 h-4" />
                  sales@clearpathrapid.com
                </a>
                <a href="tel:817-508-6232" className="flex items-center justify-center gap-2 text-sky-600 hover:text-sky-700 text-sm">
                  <Phone className="w-4 h-4" />
                  (817) 508-6232
                </a>
              </div>
            </div>
            <Button onClick={handleClose} variant="outline" className="border-slate-300" data-testid="pricing-close-btn">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="pricing-modal-title">
                Request Pricing
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600 mt-2">
                Request pricing and availability. A ClearPath Rapid Diagnostics representative will follow up promptly.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Full Name */}
                <div>
                  <Label htmlFor="pricing-name" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pricing-name"
                    name="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-11"
                    data-testid="pricing-name-input"
                  />
                </div>

                {/* Organization */}
                <div>
                  <Label htmlFor="pricing-organization" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Organization / Clinic Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pricing-organization"
                    name="organization"
                    type="text"
                    placeholder="Clinic or facility name"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                    className="h-11"
                    data-testid="pricing-organization-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="pricing-email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pricing-email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-11"
                    data-testid="pricing-email-input"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="pricing-phone" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Phone Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pricing-phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="h-11"
                    data-testid="pricing-phone-input"
                  />
                </div>
              </div>

              {/* Product of Interest */}
              <div>
                <Label htmlFor="pricing-product" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Product of Interest <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.product_interest} onValueChange={(value) => handleSelectChange('product_interest', value)}>
                  <SelectTrigger className="h-11" data-testid="pricing-product-select">
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.value} value={product.value} data-testid={`pricing-product-${product.value}`}>
                        {product.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Estimated Monthly Volume */}
              <div>
                <Label htmlFor="pricing-volume" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Estimated Monthly Volume <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.monthly_volume} onValueChange={(value) => handleSelectChange('monthly_volume', value)}>
                  <SelectTrigger className="h-11" data-testid="pricing-volume-select">
                    <SelectValue placeholder="Select volume range" />
                  </SelectTrigger>
                  <SelectContent>
                    {volumeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value} data-testid={`pricing-volume-${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message (Optional) */}
              <div>
                <Label htmlFor="pricing-message" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Message / Notes <span className="text-slate-400 font-normal">(Optional)</span>
                </Label>
                <Textarea
                  id="pricing-message"
                  name="message"
                  placeholder="Any additional information about your requirements..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  className="resize-none"
                  data-testid="pricing-message-input"
                />
              </div>

              {/* Contact Info */}
              <div className="bg-slate-50 rounded-lg p-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-sm">
                <span className="text-slate-600">Questions? Contact our sales team:</span>
                <div className="flex gap-4">
                  <a href="tel:817-508-6232" className="text-sky-600 hover:text-sky-700 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    (817) 508-6232
                  </a>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1 border-slate-300"
                  data-testid="pricing-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="pricing-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Request"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
